import type {
  IAttendanceActivity,
  IPendingAttendanceException,
} from "@/types/(my-space)/timeAttendace.interface";
import type { IShiftInfo } from "@/types/timeAttendance";
import i18n from "@/i18n";

type IClockDevice = NonNullable<IAttendanceActivity["clockInDevice"]>;
type IClockTerminal = NonNullable<IAttendanceActivity["clockInTerminal"]>;

// ── Segment type system ───────────────────────────────────────────────────────

export interface SegmentBase {
  startMs:      number;
  endMs:        number;
  durationMs:   number;
  widthPercent: number;
  leftPercent:  number;
  cssColor:     string;
  label:        string;
}

/** Where one side of a session was punched — feeds the tooltip's "via" row. */
export interface PunchSource {
  device:    IClockDevice | null;
  terminal:  IClockTerminal | null;
  /** Set when HR entered/edited the time by hand rather than a real punch. */
  updatedBy: { firstName: string; lastName: string } | null;
}

/** Work/active segments carry the session's punch sources — clock-out is
 * absent for a still-open session. */
interface SessionPunches {
  checkIn:   PunchSource;
  checkOut?: PunchSource;
}

export interface LateSegment      extends SegmentBase { kind: "late" }
export interface WorkSegment      extends SegmentBase, SessionPunches { kind: "work" }
export interface GapSegment       extends SegmentBase { kind: "gap" }
export interface RemainingSegment extends SegmentBase { kind: "remaining" }
export interface ActiveSegment    extends SegmentBase, SessionPunches { kind: "active" }
export interface ActivitySegment  extends SegmentBase {
  kind:         "activity";
  activityName: string;
  activityIcon: string;
  isLive:       boolean;
}

export type TimelineSegment =
  | LateSegment
  | WorkSegment
  | GapSegment
  | RemainingSegment
  | ActiveSegment
  | ActivitySegment;

export type SegmentKind = TimelineSegment["kind"];

export interface TimelineResult {
  segments:        TimelineSegment[];
  shiftStartMs:    number;
  shiftEndMs:      number;
  shiftDurationMs: number;
}

// ── Colours ───────────────────────────────────────────────────────────────────

const SEGMENT_COLOR: Record<Exclude<SegmentKind, "activity">, string> = {
  late:      "var(--color-warning)",
  work:      "var(--color-success)",
  gap:       "var(--color-danger)",
  remaining: "var(--color-border)",
  active:    "var(--color-success)",
};

// Room reserved past a trailing point-in-time pending marker (e.g. a
// check-out) so it isn't clipped to zero width sitting exactly at the
// window's right edge. Shared between the window-sizing pass and the
// overlay builder below so the two stay in agreement.
const MARKER_END_PADDING_MS = 5 * 60 * 1000;

// ── Pure helpers ──────────────────────────────────────────────────────────────

function punchSource(
  device:    IClockDevice | null | undefined,
  terminal:  IClockTerminal | null | undefined,
  updatedBy: PunchSource["updatedBy"] | undefined,
): PunchSource {
  return { device: device ?? null, terminal: terminal ?? null, updatedBy: updatedBy ?? null };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Parse "R G B" (e.g. "26 213 152") → "rgb(26, 213, 152)".
 * Falls back to neutral gray on malformed input.
 */
function parseRgbString(colorStr: string): string {
  const parts = colorStr.trim().split(/\s+/);
  if (parts.length >= 3) return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
  return "var(--color-border-strong)";
}

// ── Segment builder ───────────────────────────────────────────────────────────

/**
 * Pure, side-effect-free function that transforms a day's attendance data into
 * an array of typed, renderable segment descriptors relative to the shift window.
 *
 * @param dayDate       "YYYY-MM-DD" string for the day being rendered
 * @param activities    Attendance activities for that day (order does not matter)
 * @param shift         Shift configuration supplying shiftStartTime / shiftEndTime
 * @param currentTimeMs Current timestamp (Date.now()) — used for live sessions
 * @param fullDay       When true, the rendered window expands past the shift to
 *                      cover any out-of-shift activity (evening/late sessions,
 *                      an open live session) so nothing is clipped off the edge.
 *                      When false (default), the window is the shift — as before.
 */
export function buildTimelineSegments(
  dayDate:       string,
  activities:    readonly IAttendanceActivity[],
  shift:         IShiftInfo,
  currentTimeMs: number,
  fullDay:       boolean = false,
  pendingExceptions: readonly IPendingAttendanceException[] = [],
): TimelineResult {
  // 1. Anchor shift times to the specific calendar day
  const shiftStartMs = new Date(`${dayDate}T${shift.shiftStartTime}`).getTime();
  let shiftEndMs   = new Date(`${dayDate}T${shift.shiftEndTime}`).getTime();

  // Overnight shift support (e.g. 22:00 → 06:00 next day)
  if (shiftEndMs <= shiftStartMs) shiftEndMs += 86_400_000;

  // "Now" only means something for THIS row's own calendar day. Viewing a
  // past day (e.g. yesterday's row rendered today) must not treat the real
  // Date.now() as still inside it — an "ongoing" pending exception or open
  // session would otherwise stretch clean across the day boundary into
  // today, ballooning a striped overlay to span 17+ hours instead of
  // stopping at that day's end.
  const dayEndMs = new Date(`${dayDate}T23:59:59.999`).getTime();
  const effectiveNowMs = Math.min(currentTimeMs, dayEndMs);

  // Render window — the range that maps to 0–100% and drives the axis ticks.
  // Defaults to the shift; in fullDay mode it stretches to include any activity
  // — or pending exception attempt — that starts/ends outside the shift, so
  // nothing (including a pending-exception overlay) gets silently clipped off
  // an axis that stopped at the old shift end.
  let windowStartMs = shiftStartMs;
  let windowEndMs   = shiftEndMs;
  // Earliest real data point (activity or pending exception) and whether any
  // data point actually falls inside the shift — tracked so a day where
  // everything happened AFTER shiftEnd (e.g. two pending exceptions at
  // 6:05 PM / 6:17 PM against a 9–6 shift) doesn't render a mostly-dead
  // 9 AM → shiftEnd stretch before the real data even starts.
  let earliestDataMs: number | null = null;
  let hasDataInShift = false;
  const noteData = (ms: number): void => {
    if (Number.isNaN(ms)) return;
    earliestDataMs = earliestDataMs === null ? ms : Math.min(earliestDataMs, ms);
    if (ms >= shiftStartMs && ms <= shiftEndMs) hasDataInShift = true;
  };
  // Unconditional — unlike the pendingExceptions block below (whose caller,
  // AttendanceProgressBar, already forces fullDay on whenever it has any to
  // show), nothing upstream compensates for `fullDay` being left false here.
  // A CONFIRMED activity entirely outside the plain shift window (e.g. a
  // late-evening approved check-in/out against a 9–6 shift) must never be
  // invisible just because the caller didn't opt into fullDay — that clamps
  // its left%/width% to 100%/0%, an already-happened session rendering as
  // nothing at all.
  if (activities.length > 0) {
    for (const a of activities) {
      const inMs = new Date(a.clockInTime).getTime();
      if (!Number.isNaN(inMs)) {
        windowStartMs = Math.min(windowStartMs, inMs);
        noteData(inMs);
      }
      const outMs = a.clockOutTime
        ? new Date(a.clockOutTime).getTime()
        : effectiveNowMs;
      if (!Number.isNaN(outMs)) windowEndMs = Math.max(windowEndMs, outMs);
      if (a.quickActivityEndTime) {
        const qaEnd = new Date(a.quickActivityEndTime).getTime();
        if (!Number.isNaN(qaEnd)) windowEndMs = Math.max(windowEndMs, qaEnd);
      }
    }
  }
  if (fullDay && pendingExceptions.length > 0) {
    const sortedPending = [...pendingExceptions]
      .filter(
        (p) => p.attemptedTime && !Number.isNaN(new Date(p.attemptedTime).getTime()),
      )
      .sort(
        (a, b) =>
          new Date(a.attemptedTime!).getTime() - new Date(b.attemptedTime!).getTime(),
      );
    sortedPending.forEach((p, i) => {
      const attemptedMs = new Date(p.attemptedTime!).getTime();
      windowStartMs = Math.min(windowStartMs, attemptedMs);
      noteData(attemptedMs);
      const isLast = i === sortedPending.length - 1;
      // Only a still-open, unpaired check-in is genuinely "ongoing" — stretch
      // the window to now for that case. A check-out is a POINT in time (the
      // attempt already happened), not a duration, so it never pushes the
      // window past its own moment — the day's normal gap/remaining
      // rendering resumes right after it, exactly as if nothing were pending.
      if (isLast && p.requestType === 'check_in') {
        windowEndMs = Math.max(windowEndMs, effectiveNowMs);
      } else {
        // A point-in-time marker still needs a sliver of room to its right to
        // actually render (zero-width at the exact window edge is clipped to
        // nothing) — a few minutes' padding, not a stretch.
        windowEndMs = Math.max(windowEndMs, attemptedMs + MARKER_END_PADDING_MS);
      }
    });
  }
  if (earliestDataMs !== null && !hasDataInShift) {
    // Nothing at all happened during the shift itself — every data point is
    // outside it (here, after shiftEnd). Anchoring to shiftStart would just
    // render dead space (a "Late Arrival" segment spanning the entire
    // 9 AM → shiftEnd stretch for what's really a single late-evening
    // check-in), so the scale starts at the first real data point instead,
    // dropping the empty shift range entirely. Unconditional — same
    // reasoning as the activities loop above; no caller should have to opt
    // into not rendering hours of dead space.
    windowStartMs = earliestDataMs;
  }
  // A real event earlier than the plain shift start already pulled the
  // window open to exactly that point (activities loop above) — no lead-in
  // padding, so an early check-in starts flush at the left edge rather than
  // behind an empty hour.
  const shiftDurationMs = Math.max(1, windowEndMs - windowStartMs);

  // Percentage helpers — all values clamped to [0, 100], mapped over the window.
  const leftOf = (ms: number): number =>
    clamp(((ms - windowStartMs) / shiftDurationMs) * 100, 0, 100);

  const widthOf = (startMs: number, endMs: number): number => {
    const l   = leftOf(startMs);
    const raw = clamp(((Math.max(0, endMs - startMs)) / shiftDurationMs) * 100, 0, 100);
    return Math.min(raw, 100 - l); // prevent visual overflow
  };

  // Builds a base segment object
  const makeBase = (
    startMs: number,
    endMs:   number,
    cssColor: string,
    label:   string,
  ): SegmentBase => ({
    startMs,
    endMs,
    durationMs:   Math.max(0, endMs - startMs),
    leftPercent:  leftOf(startMs),
    widthPercent: widthOf(startMs, endMs),
    cssColor,
    label,
  });

  // Same as makeBase, but the RENDERED box (left%/width%) is measured from
  // `visualStart` while startMs/endMs/durationMs — what the tooltip reads —
  // stay the true, un-clipped values. For a segment whose real start
  // predates the window (late arrival vs. a padded/dropped-dead-space
  // window), clipping the box to the visible area must not also lie about
  // when the thing actually started — "Late by 09:05" on the row and "01h"
  // in its own tooltip would otherwise disagree.
  const makeBaseVisualClip = (
    realStart:   number,
    visualStart: number,
    endMs:       number,
    cssColor:    string,
    label:       string,
  ): SegmentBase => ({
    startMs: realStart,
    endMs,
    durationMs:   Math.max(0, endMs - realStart),
    leftPercent:  leftOf(visualStart),
    widthPercent: widthOf(visualStart, endMs),
    cssColor,
    label,
  });

  const segments: TimelineSegment[] = [];

  const push = (seg: TimelineSegment): void => {
    // A quick activity can be logged with its start/end the same instant (a
    // zero-duration log, not just a short one) — still a real, meaningful
    // event the UI needs to render as a minWidth-floored block (see
    // SegmentBlock), so it's exempt from the "nothing to show" duration
    // guard that correctly drops a zero-length late/gap/remaining segment.
    if (seg.durationMs > 0 || seg.kind === "activity") segments.push(seg);
  };

  // 2. Empty day — shift renders as "remaining", clipped to the window (the
  //    window may have dropped a dead stretch of the shift entirely — see
  //    the !hasDataInShift branch above — so an un-clipped shift range here
  //    would render as a false full-width bar).
  if (activities.length === 0) {
    const remStart = Math.max(shiftStartMs, windowStartMs);
    const remEnd = Math.min(shiftEndMs, windowEndMs);
    if (remEnd > remStart) {
      push({ kind: "remaining", ...makeBase(remStart, remEnd, SEGMENT_COLOR.remaining, "Remaining Shift") });
    }
    return { segments, shiftStartMs: windowStartMs, shiftEndMs: windowEndMs, shiftDurationMs };
  }

  // 3. Sort chronologically
  const sorted = [...activities].sort(
    (a, b) => new Date(a.clockInTime).getTime() - new Date(b.clockInTime).getTime(),
  );

  // cursor tracks the end of the last rendered segment
  let cursor  = shiftStartMs;
  // liveEndMs: cap for any ongoing session (current time, but never past the
  // rendered window — the shift end normally, or the extended edge in fullDay)
  const liveEndMs = Math.min(effectiveNowMs, windowEndMs);
  // A pending CHECK-OUT attempt on the still-open session means the person
  // tried to clock out — drawing "Active Work" straight through that point
  // to "now" claims they're still working, unresolved either way. The live
  // segment stops there instead; the pending overlay marks the attempt
  // itself, and the (gray) remaining-shift segment picks up after it, same
  // as if nothing were open at all.
  const earliestPendingCheckoutMs = pendingExceptions
    .filter((p) => p.requestType === "check_out" && p.attemptedTime)
    .map((p) => new Date(p.attemptedTime!).getTime())
    .filter((ms) => !Number.isNaN(ms))
    .sort((a, b) => a - b)[0];

  sorted.forEach((act, i) => {
    const inMs       = new Date(act.clockInTime).getTime();
    const rawOutMs   = act.clockOutTime ? new Date(act.clockOutTime).getTime() : null;
    const isLast     = i === sorted.length - 1;
    const hasActivity = act.quickActivity != null && act.quickActivityId != null;
    const checkIn  = punchSource(act.clockInDevice, act.clockInTerminal, act.clockInUpdatedBy);

    // ── Late arrival (first record only) ──────────────────────────────────
    if (i === 0 && inMs > shiftStartMs) {
      // The window can start LATER than the real shift start (the
      // !hasDataInShift branch above drops a dead pre-data stretch
      // entirely) — anchoring this segment to the raw shiftStartMs then
      // would compute a width against a duration the window can't actually
      // hold, clamped to 100% and silently spanning the WHOLE bar (only
      // visually hidden by whatever draws on top of it, not actually
      // narrow — a false "Late Arrival" hover hit-box over the entire row).
      // Clip the rendered start to the window; the "Late by" figure shown
      // elsewhere already comes from the server, unaffected by this.
      const visualLateStart = Math.max(shiftStartMs, windowStartMs);
      push({
        kind: "late",
        ...makeBaseVisualClip(shiftStartMs, visualLateStart, inMs, SEGMENT_COLOR.late, "Late Arrival"),
      });
      cursor = inMs; // advance so the gap check below doesn't double-render this range
    }

    // ── Unaccounted gap before this clockIn ───────────────────────────────
    if (inMs > cursor) {
      push({ kind: "gap", ...makeBase(cursor, inMs, SEGMENT_COLOR.gap, "Unaccounted Gap") });
    }

    if (rawOutMs === null) {
      // ── No clockOut: user is currently checked IN (working) — unless a
      // pending checkout attempt already exists after this check-in, in
      // which case the live segment stops there (see above).
      const activeEndMs =
        earliestPendingCheckoutMs != null && earliestPendingCheckoutMs > inMs
          ? Math.min(earliestPendingCheckoutMs, liveEndMs)
          : liveEndMs;
      push({ kind: "active", checkIn, ...makeBase(inMs, activeEndMs, SEGMENT_COLOR.active, "Active Work") });
      cursor = activeEndMs;
    } else {
      // ── Completed session: clockIn → clockOut is always green work ────────
      push({
        kind: "work",
        checkIn,
        checkOut: punchSource(act.clockOutDevice, act.clockOutTerminal, act.clockOutUpdatedBy),
        ...makeBase(inMs, rawOutMs, SEGMENT_COLOR.work, "Work"),
      });
      cursor = rawOutMs;

      if (hasActivity) {
        // ── Activity period ───────────────────────────────────────────────
        // Prefer backend-supplied timestamps; fall back to derived window.
        const actStartMs = act.quickActivityStartTime
          ? new Date(act.quickActivityStartTime).getTime()
          : rawOutMs;

        const hasRealEnd = !!act.quickActivityEndTime;
        const nextInMs   = isLast ? null : new Date(sorted[i + 1].clockInTime).getTime();
        const actEndMs   = hasRealEnd
          ? new Date(act.quickActivityEndTime!).getTime()
          : (nextInMs ?? liveEndMs);

        const isLive = !hasRealEnd && isLast;

        push({
          kind:         "activity",
          activityName: act.quickActivity!.name,
          activityIcon: act.quickActivity!.icon,
          isLive,
          ...makeBase(actStartMs, actEndMs, parseRgbString(act.quickActivity!.color), act.quickActivity!.name),
        });
        cursor = Math.max(cursor, actEndMs);
      }
    }
  });

  // 4. Remaining shift after cursor (only up to the actual shift end — never
  //    into the extended fullDay tail, which would be a phantom "remaining")
  if (cursor < shiftEndMs) {
    push({ kind: "remaining", ...makeBase(cursor, shiftEndMs, SEGMENT_COLOR.remaining, "Remaining Shift") });
  }

  return { segments, shiftStartMs: windowStartMs, shiftEndMs: windowEndMs, shiftDurationMs };
}

// ── Pending-exception overlay ─────────────────────────────────────────────────
//
// Deliberately separate from the segment builder above: a pending exception
// has no confirmed TimeAttendance row yet, so it's not part of `activities`
// at all — it's rendered as an overlay stripe on top of whatever the base
// timeline already shows (gap/remaining/etc.), not a replacement for it.

export interface PendingOverlaySegment {
  id: number;
  leftPercent: number;
  widthPercent: number;
  label: string;
  cssColor: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  /** True for the last (still-open) stripe — end is "now", not a fixed time. */
  isLive: boolean;
}

// A check-out (or, once the backend carries one, a quick-activity END) is a
// POINT in time — the attempt already happened — not a duration, so it never
// draws a stripe stretching after itself. Only a still-open, unpaired
// check-in (or a future quick-activity START) is genuinely ongoing and draws
// a filled stripe forward. Widening this map is how a quick-activity pending
// type slots in later: add its two request-type strings here with `fill:
// true` for the start and `fill: false` for the end, nothing else changes.
const PENDING_KIND_FILLS: Record<string, boolean> = {
  check_in: true,
  check_out: false,
};

/** Minimum on-screen width for a point-in-time marker (a check-out), as a
 * percent of the window — wide enough to stay hoverable at any zoom. */
const MARKER_WIDTH_PERCENT = 1.2;

/**
 * Builds overlay stripes for pending exceptions that fall (at least partly)
 * within [windowStartMs, windowEndMs] — the same render window
 * `buildTimelineSegments` computed.
 *
 * A pending check-in draws a filled stripe forward to the NEXT exception (a
 * paired check-out — the claimed work span, exactly like a confirmed "work"
 * segment) or, if it's the last/only one, to now (still open). A pending
 * check-out draws only a thin marker at its own instant — it is the END of
 * that span, not a new span, so nothing renders after it: the day's normal
 * gap/remaining rendering takes over from there, same as if nothing were
 * pending at all.
 */
export function buildPendingOverlaySegments(
  pendingExceptions: readonly IPendingAttendanceException[],
  windowStartMs: number,
  windowEndMs: number,
  currentTimeMs: number,
): PendingOverlaySegment[] {
  if (!pendingExceptions.length) return [];
  const windowDurationMs = Math.max(1, windowEndMs - windowStartMs);
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  const sorted = [...pendingExceptions]
    .filter((e) => e.attemptedTime && !Number.isNaN(new Date(e.attemptedTime).getTime()))
    .sort(
      (a, b) => new Date(a.attemptedTime!).getTime() - new Date(b.attemptedTime!).getTime(),
    );

  const overlays: PendingOverlaySegment[] = [];
  sorted.forEach((exception, i) => {
    const startMs = new Date(exception.attemptedTime!).getTime();
    if (startMs >= windowEndMs || startMs < windowStartMs) return;

    const isCheckOut = exception.requestType === "check_out";
    const fills = PENDING_KIND_FILLS[exception.requestType ?? ""] ?? true;
    const next = sorted[i + 1];

    const leftPercent = ((startMs - windowStartMs) / windowDurationMs) * 100;

    if (!fills) {
      // Point-in-time marker — no stretch, nothing after it.
      const widthPercent = Math.min(MARKER_WIDTH_PERCENT, 100 - leftPercent);
      if (widthPercent <= 0) return;
      overlays.push({
        id: exception.id,
        leftPercent,
        widthPercent,
        label: isCheckOut
          ? i18n.t("common:attendance_timeline.pending_checkout_approval")
          : i18n.t("common:attendance_timeline.pending_activity_end"),
        cssColor: isCheckOut ? SEGMENT_COLOR.gap : SEGMENT_COLOR.work,
        startMs,
        endMs: startMs,
        durationMs: 0,
        isLive: false,
      });
      return;
    }

    // Filled stripe — the claimed span, ending at the next exception (of any
    // kind — it's what closes this one out) or, unpaired, at now.
    const rawEndMs = next ? new Date(next.attemptedTime!).getTime() : currentTimeMs;
    const endMs = Math.min(rawEndMs, windowEndMs);
    if (endMs <= windowStartMs) return;

    const clippedStart = clamp(startMs, windowStartMs, windowEndMs);
    const widthPercent = Math.min(
      ((endMs - clippedStart) / windowDurationMs) * 100,
      100 - leftPercent,
    );
    if (widthPercent <= 0) return;

    overlays.push({
      id: exception.id,
      leftPercent,
      widthPercent,
      label: i18n.t("common:attendance_timeline.pending_check_in_approval"),
      cssColor: SEGMENT_COLOR.work,
      startMs,
      endMs,
      durationMs: Math.max(0, endMs - startMs),
      isLive: !next,
    });
  });
  return overlays;
}
